import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, concatMap, debounceTime, Observable, of, tap } from 'rxjs';
import {
  BaseDocLineDto,
  CustomerDeliveryNoteDto,
  CustomerDeliveryNoteService,
  CustomerDeliveryNoteToInvoiceDto,
  CustomerInvoiceDto,
  CustomerInvoiceService,
} from '@digiwall/goliath-documents/proxy';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { DeliveryNoteToInvoiceGridComponent } from './delivery-note-to-invoice-grid/delivery-note-to-invoice-grid.component';
import { CustomerInvoicePreviewGridComponent } from './customer-invoice-preview-grid/customer-invoice-preview-grid.component';
import { AsyncPipe, CommonModule } from '@angular/common';
import { LocalizationService } from '@abp/ng.core';
import { OdysseyOffcanvas } from '@digiwall/core';

@Component({
  selector: 'document-customer-invoice-creation-dialog',
  templateUrl: './customer-invoice-creation-dialog.component.html',
  styleUrl: './customer-invoice-creation-dialog.component.scss',
  imports: [
    DialogBoxComponent,
    DeliveryNoteToInvoiceGridComponent,
    CustomerInvoicePreviewGridComponent,
    AsyncPipe,
    CommonModule,
  ],
})
export class CustomerInvoiceCreationDialogComponent implements OnInit {
  @Input() deliveryNoteId: string;
  @Output() customerInvoiceCreated = new EventEmitter<CustomerInvoiceDto>();

  deliveryNotes$: Observable<CustomerDeliveryNoteToInvoiceDto[]>;

  selectedDeliveryNotes$ = new BehaviorSubject<CustomerDeliveryNoteDto[]>([]);

  previewInvoiceLines$ = new BehaviorSubject<BaseDocLineDto[]>([]);

  invoiceDate: string;
  offCanvaService = inject(OdysseyOffcanvas);

  constructor(
    private deliveryNoteService: CustomerDeliveryNoteService,
    private customerInvoiceService: CustomerInvoiceService,
    private localizationService: LocalizationService,
  ) {
    this.selectedDeliveryNotes$
      .pipe(
        debounceTime(200),
        concatMap(selectedDeliveryNotes => {
          const selectedIds = new Set<string>(selectedDeliveryNotes.map(note => note.id));
          const currentLines = this.previewInvoiceLines$.getValue();

          // 1. Identify lines to keep (still selected)
          const updatedLines = currentLines.filter(line => selectedIds.has(line.docId));

          // 2. Identify new documents to add (not already present)
          const currentIds = new Set<string>(updatedLines.map(line => line.docId));
          const docsToAdd = selectedDeliveryNotes.filter(x => !currentIds.has(x.id));

          // 3. If no new lines, just update previewInvoiceLines$ with filtered result
          if (docsToAdd.length === 0) {
            const sortedLines = this.renumberAndSortLines([...updatedLines]);
            this.previewInvoiceLines$.next(sortedLines);
            return of([]);
          }

          // 4. Otherwise fetch preview lines and merge
          return this.deliveryNoteService.customerInvoicePreview(docsToAdd.map(x => x.id)).pipe(
            tap(newPreviewLines => {
              const mergedLines = this.renumberAndSortLines([...updatedLines, ...newPreviewLines]);
              this.previewInvoiceLines$.next(mergedLines);
            }),
          );
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.deliveryNotes$ = this.deliveryNoteService.deliveryNotesToInvoice(this.deliveryNoteId);

    this.customerInvoiceService.getDefaultInvoiceDate(this.deliveryNoteId).subscribeOnce(res => {
      this.invoiceDate = res.date;
    });
  }

  deliveryNoteSelectionChange(event) {
    this.selectedDeliveryNotes$.next(event);
  }

  save() {
    const selectedDeliveryNoteIds = this.selectedDeliveryNotes$.value.map(
      deliveryNote => deliveryNote.id,
    );

    this.customerInvoiceService
      .createFromDeliveryNotes(selectedDeliveryNoteIds)
      .subscribe(response => {
        if (response) {
          this.customerInvoiceCreated.emit(response);
          this.offCanvaService.dismiss();
        }
      });
  }

  public get isAntidated() {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return new Date(this.invoiceDate) < startOfDay;
  }

  public get dialogBoxTitle() {
    return `${this.localizationService.instant('Doc::DeliveryNote:ToInvoice')} - ${this.localizationService.instant('Doc::CustomerInvoice:InvoiceDate')} : ${new Date(this.invoiceDate).toLocaleDateString()}`;
  }

  renumberAndSortLines(previewInvoiceLines: BaseDocLineDto[]): BaseDocLineDto[] {
    const groupedByDocId: Map<string, SplitedReference[]> = new Map();
    const docDisplayNames: Map<string, string> = new Map();

    // 1. Découpage et regroupement par docId
    for (const line of previewInvoiceLines) {
      const parts = line.displayReference.split('.');
      const reference: SplitedReference = {
        line,
        part1: parts[0],
        part2: parts[1],
        part3: parts[2],
        part4: parts[3],
      };

      if (!groupedByDocId.has(line.docId)) {
        groupedByDocId.set(line.docId, []);
      }
      groupedByDocId.get(line.docId)!.push(reference);

      if (parts.length == 1 && !docDisplayNames.has(line.docId)) {
        docDisplayNames.set(line.docId, line.description);
      }
    }

    // 2. Trier les docIds et renuméroter part1 par ordre
    const sortedDocIds = Array.from(groupedByDocId.keys()).sort((a, b) =>
      docDisplayNames.get(a).localeCompare(docDisplayNames.get(b)),
    );
    let newPart1 = 1;
    const finalList: SplitedReference[] = [];

    for (const docId of sortedDocIds) {
      const references = groupedByDocId.get(docId)!;

      // Trier les lignes internes au docId selon les parties
      references.sort((a, b) => {
        return (
          (a.part1 ?? '').localeCompare(b.part1 ?? '') ||
          (a.part2 ?? '').localeCompare(b.part2 ?? '') ||
          (a.part3 ?? '').localeCompare(b.part3 ?? '') ||
          (a.part4 ?? '').localeCompare(b.part4 ?? '')
        );
      });

      for (const ref of references) {
        // Recréer displayReference avec nouvelle part1
        const newRefParts = [newPart1.toString()];
        if (ref.part2) newRefParts.push(ref.part2);
        if (ref.part3) newRefParts.push(ref.part3);
        if (ref.part4) newRefParts.push(ref.part4);
        ref.line.displayReference = newRefParts.join('.');
        finalList.push(ref);
      }

      newPart1++; // Incrément pour docId suivant
    }

    return finalList.map(ref => ref.line);
  }
}

export interface SplitedReference {
  line: BaseDocLineDto;
  part1?: string;
  part2?: string;
  part3?: string;
  part4?: string;
  //isInformationLine: boolean;
}

export interface NumberedLine {
  line: BaseDocLineDto;
  number: string;
}
